import { useEffect } from "react";
import i18next, { languageMap } from "@/i18n";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";

// Brand suffix appended to the document title, mirroring the marketing-site
// title template ("<page> | Ahead Health"). It's a brand constant, identical
// across locales, so it lives in code rather than in translation copy.
const BRAND_SUFFIX = " | Ahead Health";

/**
 * Keeps `<html lang>` and the document `<title>` / `<meta name="description">`
 * in sync with the active language.
 *
 * Driven off the shared booking-flow store `language` — that's the source the
 * in-app language switcher updates (`setLanguage`) and that the body renders
 * from. (The store does not call `i18next.changeLanguage`, so listening to
 * i18next or to a local useLanguage instance missed in-app switches: the body
 * went German while the title/lang stayed English.)
 *
 * The title text is resolved with an explicit `{ lng }` so it's correct even
 * when the i18next current language hasn't been switched; both locales are
 * preloaded, and the `loaded` listener covers the first-paint race.
 *
 * Previously the title was only set via an undefined `window.setPageTitle`
 * global (a silent no-op) and `<html lang>` was never updated — so the German
 * booking flow (`?locale=de`) shipped `<html lang="en">` and an English title.
 */
export const useDocumentMeta = () => {
  const language = useBookingFlowStore((s) => s.language);

  useEffect(() => {
    const lng = languageMap[language] || "en";
    document.documentElement.lang = lng;

    const apply = () => {
      const base = i18next.t("booking.pageTitle", { lng });
      document.title = `${base}${BRAND_SUFFIX}`;
      const description = document.querySelector('meta[name="description"]');
      if (description) description.setAttribute("content", base);
    };

    apply();
    // Cover the first-paint race before i18next finishes initialising.
    i18next.on("loaded", apply);
    return () => i18next.off("loaded", apply);
  }, [language]);
};
